import React, { useState, useMemo } from 'react';
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, ComboboxButton, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';
import useTimezone from '../hooks/useTimezone';

const ALL_TIMEZONES = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
})();

/**
 * Full-size timezone combobox for settings pages.
 */
export const TimezoneCombobox = () => {
  const { user, refreshUser } = useAuth();
  const { effectiveTimezone, browserTimezone } = useTimezone();
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    if (!query) return ALL_TIMEZONES;
    const q = query.toLowerCase();
    return ALL_TIMEZONES.filter((tz) => tz.toLowerCase().includes(q));
  }, [query]);

  const handleChange = async (tz) => {
    if (!tz || tz === user?.timezone) return;
    setSaving(true);
    try {
      await apiClient.patch('/api/auth/timezone', { timezone: tz });
      await refreshUser();
      toast.success(`Timezone set to ${tz}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timezone');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-sm space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Timezone
      </label>
      <Combobox value={effectiveTimezone} onChange={handleChange} onClose={() => setQuery('')}>
        <div className="relative">
          <ComboboxInput
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            displayValue={(tz) => tz || ''}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search timezone..."
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
          </ComboboxButton>
        </div>
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full max-w-sm overflow-auto rounded-lg bg-white dark:bg-gray-800 py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
          {filtered.length === 0 ? (
            <div className="px-4 py-2 text-gray-500 dark:text-gray-400">No timezones found</div>
          ) : (
            filtered.map((tz) => (
              <ComboboxOption
                key={tz}
                value={tz}
                className="group relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 dark:text-white data-[focus]:bg-primary-500 data-[focus]:text-white"
              >
                <span className="block truncate group-data-[selected]:font-semibold">{tz}</span>
                <span className="absolute inset-y-0 left-0 hidden items-center pl-3 group-data-[selected]:flex text-primary-600 group-data-[focus]:text-white">
                  <CheckIcon className="h-4 w-4" />
                </span>
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>
      {!user?.timezone && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Using browser default: {browserTimezone}
        </p>
      )}
      {saving && (
        <p className="text-xs text-primary-600 dark:text-primary-400">Saving...</p>
      )}
    </div>
  );
};

/**
 * Compact inline timezone picker — shows abbreviation, click to change.
 */
export const TimezoneInline = () => {
  const { user, refreshUser } = useAuth();
  const { abbreviation, effectiveTimezone } = useTimezone();
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    if (!query) return ALL_TIMEZONES;
    const q = query.toLowerCase();
    return ALL_TIMEZONES.filter((tz) => tz.toLowerCase().includes(q));
  }, [query]);

  const handleChange = async (tz, close) => {
    if (!tz || tz === user?.timezone) return;
    setSaving(true);
    try {
      await apiClient.patch('/api/auth/timezone', { timezone: tz });
      await refreshUser();
      toast.success(`Timezone set to ${tz}`);
      close();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timezone');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover className="relative">
      <PopoverButton className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer focus:outline-none transition-colors" title={`Timezone: ${effectiveTimezone} — click to change`}>
        {saving ? '...' : abbreviation}
      </PopoverButton>
      <PopoverPanel className="absolute right-0 z-20 mt-2 w-72 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/10 dark:ring-white/10 p-2">
        {({ close }) => (
          <Combobox value={effectiveTimezone} onChange={(tz) => handleChange(tz, close)} onClose={() => setQuery('')}>
            <div className="relative">
              <ComboboxInput
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1.5 pl-3 pr-8 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                displayValue={(tz) => tz || ''}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search timezone..."
                autoFocus
              />
              <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
              </ComboboxButton>
            </div>
            <ComboboxOptions static className="mt-1 max-h-48 overflow-auto rounded-md text-sm">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-gray-500 dark:text-gray-400">No timezones found</div>
              ) : (
                filtered.map((tz) => (
                  <ComboboxOption
                    key={tz}
                    value={tz}
                    className="group relative cursor-pointer select-none py-1.5 pl-8 pr-3 text-gray-900 dark:text-white data-[focus]:bg-primary-500 data-[focus]:text-white rounded"
                  >
                    <span className="block truncate group-data-[selected]:font-semibold">{tz}</span>
                    <span className="absolute inset-y-0 left-0 hidden items-center pl-2 group-data-[selected]:flex text-primary-600 group-data-[focus]:text-white">
                      <CheckIcon className="h-3.5 w-3.5" />
                    </span>
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Combobox>
        )}
      </PopoverPanel>
    </Popover>
  );
};
